import { AccountPageLayout } from "components/common/AccountPageLayout";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { ValidatedTextField } from "components/common/inputs/validatedTextField";
import {
  PeakShavingInputs,
  PeakShavingOutputs,
  UnitType,
  useRunPeakShavingScenariosMutation,
} from "src/store/api/generatedApi";
import { useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { motion, AnimatePresence } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BoltIcon from "@mui/icons-material/Bolt";
import CalculateIcon from "@mui/icons-material/Calculate";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

const DEFAULT_INPUTS: PeakShavingInputs = {
  mean_electricity_cost_mwh: 80,
  peak_electricity_cost_mwh: 360,
  basket_per_tonne_material_cost_diff: 15,
  energy_usage_per_tonne_mwh: 0.5,
  basket_heat_loss_per_tonne_mwh: 0.007,
  increased_production_percent_per_basket_removed: 0.08,
  expected_variable_profit: 180,
};

interface InputConfig {
  key: keyof PeakShavingInputs;
  labelKey: string;
  unitType: UnitType;
  decimalPlaces: number;
  min?: number;
  unitLabel?: string;
}

const INPUT_CONFIGS: InputConfig[] = [
  {
    key: "mean_electricity_cost_mwh",
    labelKey: "meanElectricityCostMwh",
    unitType: "specific_energy_cost",
    decimalPlaces: 2,
    min: 0,
  },
  {
    key: "peak_electricity_cost_mwh",
    labelKey: "peakElectricityCostMwh",
    unitType: "specific_energy_cost",
    decimalPlaces: 2,
    min: 0,
  },
  {
    key: "basket_per_tonne_material_cost_diff",
    labelKey: "basketPerTonneMaterialCostDiff",
    unitType: "specific_cost",
    decimalPlaces: 2,
  },
  {
    key: "energy_usage_per_tonne_mwh",
    labelKey: "energyUsagePerTonneMwh",
    unitType: "energy",
    decimalPlaces: 3,
    min: 0,
    unitLabel: "MWh/t",
  },
  {
    key: "basket_heat_loss_per_tonne_mwh",
    labelKey: "basketHeatLossPerTonneMwh",
    unitType: "energy",
    decimalPlaces: 4,
    min: 0,
    unitLabel: "MWh/t",
  },
  {
    key: "increased_production_percent_per_basket_removed",
    labelKey: "increasedProductionPercentPerBasketRemoved",
    unitType: "mass_fraction",
    decimalPlaces: 2,
    min: 0,
  },
  {
    key: "expected_variable_profit",
    labelKey: "expectedVariableProfit",
    unitType: "specific_cost",
    decimalPlaces: 2,
  },
];

export const PeakShavingPage = () => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const tenantName = useTenant();
  const formatUnits = useUnitsFormatter(false);
  const { format } = useNumberSerialiser({ decimalPlaces: 2 });

  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [results, setResults] = useState<PeakShavingOutputs | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const [runAnalysis] = useRunPeakShavingScenariosMutation();

  const handleInputChange =
    (key: keyof PeakShavingInputs) => (value: number) => {
      setInputs((prev) => ({ ...prev, [key]: value }));
    };

  const handleRunAnalysis = async () => {
    setIsCalculating(true);
    const [result] = await Promise.all([
      runAnalysis({
        tenantName,
        peakShavingInputs: inputs,
      }),
      new Promise((resolve) => setTimeout(resolve, 1300)),
    ]);
    if ("data" in result && result.data) {
      setResults(result.data);
    }
    setIsCalculating(false);
  };

  return (
    <AccountPageLayout title={t("peakShavingAnalysis")}>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: `0 4px 20px ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )}`,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" gap={1.5} mb={3}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <BoltIcon sx={{ color: "primary.main", fontSize: 24 }} />
                    </Box>
                    <Typography variant="h5" fontWeight="fontWeightSemiBold">
                      {t("inputParameters")}
                    </Typography>
                  </Stack>

                  <Stack spacing={2.5}>
                    {INPUT_CONFIGS.map((config, index) => (
                      <motion.div
                        key={config.key}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <PeakShavingInput
                          config={config}
                          value={inputs[config.key]}
                          onChange={handleInputChange(config.key)}
                        />
                      </motion.div>
                    ))}
                  </Stack>

                  <Box sx={{ mt: 4 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => void handleRunAnalysis()}
                      disabled={isCalculating}
                      startIcon={
                        isCalculating ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <CalculateIcon />
                        )
                      }
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: "none",
                        fontSize: 16,
                        fontWeight: "fontWeightSemiBold",
                        boxShadow: `0 4px 12px ${alpha(
                          theme.palette.primary.main,
                          0.3
                        )}`,
                        "&:hover": {
                          boxShadow: `0 6px 16px ${alpha(
                            theme.palette.primary.main,
                            0.4
                          )}`,
                        },
                      }}
                    >
                      {isCalculating ? t("loading") : t("runAnalysis")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: `0 4px 20px ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )}`,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  height: "100%",
                  minHeight: 400,
                }}
              >
                <CardContent sx={{ p: 3, height: "100%" }}>
                  <Stack direction="row" alignItems="center" gap={1.5} mb={3}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                      }}
                    >
                      <TrendingDownIcon
                        sx={{ color: "info.main", fontSize: 24 }}
                      />
                    </Box>
                    <Typography variant="h5" fontWeight="fontWeightSemiBold">
                      {t("analysisResults")}
                    </Typography>
                  </Stack>

                  <AnimatePresence mode="wait">
                    {results ? (
                      <motion.div
                        key="results"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Stack spacing={3}>
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.grey[100], 0.5),
                                border: `1px solid ${theme.palette.grey[200]}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                mb={0.5}
                              >
                                {t("productionCostSavingsFromBasketRemoval")}
                              </Typography>
                              <Typography
                                variant="h5"
                                fontWeight="fontWeightBold"
                                sx={{
                                  fontFamily: "Source Code Pro, monospace",
                                }}
                              >
                                {format(
                                  results.production_cost_savings_per_tonne_from_basket_removal
                                )}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 0.5 }}
                                >
                                  {formatUnits("specific_cost", false)}
                                </Typography>
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.grey[100], 0.5),
                                border: `1px solid ${theme.palette.grey[200]}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                mb={0.5}
                              >
                                {t("productionCostSavingsFromPeakShaving")}
                              </Typography>
                              <Typography
                                variant="h5"
                                fontWeight="fontWeightBold"
                                sx={{
                                  fontFamily: "Source Code Pro, monospace",
                                }}
                              >
                                {format(
                                  results.production_cost_savings_per_tonne_from_peak_shaving
                                )}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 0.5 }}
                                >
                                  {formatUnits("specific_cost", false)}
                                </Typography>
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.grey[100], 0.5),
                                border: `1px solid ${theme.palette.grey[200]}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                mb={0.5}
                              >
                                {t("expectedVariableProfit")}
                              </Typography>
                              <Typography
                                variant="h5"
                                fontWeight="fontWeightBold"
                                sx={{
                                  fontFamily: "Source Code Pro, monospace",
                                }}
                              >
                                {format(
                                  results.production_expected_variable_profit
                                )}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 0.5 }}
                                >
                                  {formatUnits("specific_cost", false)}
                                </Typography>
                              </Typography>
                            </Box>
                          </Stack>

                          <Divider />

                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              mb={2}
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              {t("recommendation")}
                            </Typography>
                            <Stack spacing={2}>
                              <RecommendationItem
                                label={t("shouldPeakShaveAndRunFaster")}
                                value={results.should_peak_shave_and_run_faster}
                              />
                              <RecommendationItem
                                label={t("shouldPeakShaveAndProduceLess")}
                                value={
                                  results.should_peak_shave_and_produce_less
                                }
                              />
                              <RecommendationItem
                                label={t("shouldUseExtraBasketAndProduceLess")}
                                value={
                                  results.should_use_extra_basket_and_produce_less
                                }
                              />
                            </Stack>
                          </Box>
                        </Stack>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 300,
                            color: "text.secondary",
                          }}
                        >
                          <CalculateIcon
                            sx={{ fontSize: 64, mb: 2, opacity: 0.3 }}
                          />
                          <Typography variant="body1" textAlign="center">
                            {t("runAnalysis")}
                          </Typography>
                          <Typography
                            variant="body2"
                            textAlign="center"
                            sx={{ opacity: 0.7 }}
                          >
                            {t("enterParametersToSeeResults")}
                          </Typography>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </AccountPageLayout>
  );
};

const PeakShavingInput = ({
  config,
  value,
  onChange,
}: {
  config: InputConfig;
  value: number;
  onChange: (value: number) => void;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const formatUnits = useUnitsFormatter(false);
  const serialiser = useNumberSerialiser({
    decimalPlaces: config.decimalPlaces,
    min: config.min,
  });

  return (
    <ValidatedTextField
      value={value}
      setValue={onChange}
      serialiser={serialiser}
      label={t(config.labelKey)}
      textFieldProps={{
        fullWidth: true,
        size: "small",
        slotProps: {
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="body2" color="text.secondary">
                  {config.unitLabel ?? formatUnits(config.unitType, false)}
                </Typography>
              </InputAdornment>
            ),
          },
        },
        sx: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
            },
          },
        },
      }}
    />
  );
};

const RecommendationItem = ({
  label,
  value,
}: {
  label: string;
  value: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderRadius: 2,
          bgcolor: value
            ? alpha(theme.palette.success.main, 0.08)
            : alpha(theme.palette.grey[500], 0.08),
          border: `1px solid ${
            value
              ? alpha(theme.palette.success.main, 0.2)
              : alpha(theme.palette.grey[500], 0.15)
          }`,
          transition: "all 0.2s ease",
        }}
      >
        <Typography variant="body1" fontWeight="fontWeightMedium">
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          {value ? (
            <>
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 22 }} />
              <Typography
                variant="body2"
                fontWeight="fontWeightSemiBold"
                sx={{ color: "success.main" }}
              >
                {t("recommended")}
              </Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              <Typography
                variant="body2"
                fontWeight="fontWeightSemiBold"
                sx={{ color: "text.secondary" }}
              >
                {t("notRecommended")}
              </Typography>
            </>
          )}
        </Stack>
      </Box>
    </motion.div>
  );
};
